import { useState, useRef, useEffect, type ComponentProps } from "react";
import { FA6Icon } from "@/primitives/FA6Icon";
import { Button } from "@/primitives/Button";
import { Tooltip } from "@/primitives/Tooltip";

const COPY_FEEDBACK: Record<"copied" | "failed", { icon: string; message: string }> = {
	copied: { icon: "check", message: "tooltip.copied" },
	failed: { icon: "triangle-exclamation", message: "tooltip.copyNotSupported" },
};

/** How long the button reports the outcome before falling back to its normal label. */
const FEEDBACK_MS = 2000;

export interface CopyButtonProps extends Omit<ComponentProps<typeof Tooltip.Trigger>, "children"> {
	text: string;
}

/**
 * Positions itself nowhere — pass `className` to place it, or `render` to swap the button it
 * draws, e.g. `render={<InputGroup.Button/>}` for a cell inside an input group.
 */
export function CopyButton({
	text,
	render = <Button variant="secondary" size="sm" />,
	...triggerProps
}: CopyButtonProps) {
	// navigator.clipboard exists only in a secure context, and writeText still rejects
	// when the document has no focus — both end up as the "failed" state.
	const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
	const resetTimeout = useRef<number | undefined>(undefined);

	useEffect(() => () => window.clearTimeout(resetTimeout.current), []);

	const copy = () => {
		const written = navigator.clipboard
			? navigator.clipboard.writeText(text)
			: Promise.reject(new Error("clipboard unavailable"));

		void written
			.then(
				() => setState("copied"),
				() => setState("failed"),
			)
			.finally(() => {
				window.clearTimeout(resetTimeout.current);
				resetTimeout.current = window.setTimeout(() => setState("idle"), FEEDBACK_MS);
			});
	};

	const feedback = state === "idle" ? undefined : COPY_FEEDBACK[state];
	const label = messages[feedback?.message ?? "tooltip.copy"];

	return (
		<Tooltip.Root>
			<Tooltip.Trigger {...triggerProps} render={render} aria-label={label} onClick={copy}>
				<FA6Icon icon={feedback?.icon ?? "copy"} variant="regular" fixedWidth />
			</Tooltip.Trigger>
			<Tooltip.Popup>{label}</Tooltip.Popup>
		</Tooltip.Root>
	);
}
